import type { ConfigDrivenReportConfig } from "./types";
import { useConfigDrivenReport } from "./index";
import type { ConfigDrivenReportResult } from "./types";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BuildIcon from "@mui/icons-material/Build";
import PeopleIcon from "@mui/icons-material/People";
import { USER_FIELD_KEYS } from "@hooks/use-get/users/types";

/**
 * ========================================
 * Consolidated Report Configurations
 * ========================================
 * 
 * All report configurations are defined here as a single source of truth.
 * Each config includes:
 * - id, title, description: Metadata for UI and identification
 * - report_filters: Available report filter fields for this specific report
 *   (replaces global filters.config.ts - filters now belong per-report)
 * - permissions: Role-based access control (optional)
 * - dataSources: API fetch configuration
 * - charts: Visualization definitions
 * - statCards: Summary metric cards
 * 
 * This allows:
 * - Dynamic navigation generation from REPORTS object keys
 * - Centralized org-level customization point
 * - Per-report filter customization (matches MetaBox DB row structure)
 * - Easy addition of new reports (add to REPORTS object)
 * 
 * MIGRATION NOTE: report_filters array is now part of each ConfigDrivenReportConfig.
 * The separate filters.config.ts is deprecated. Use report.report_filters instead:
 *   - Hook reads filters from active report config: config.report_filters[]
 *   - Field metadata still fetched from WordPress config API
 *   - Each report can have different available filter fields
 */

// ============================================
// INVENTORY AGING
// ============================================

export const INVENTORY_AGING_CONFIG = {
  id: "inventoryAging",
  title: "Inventory Aging",
  description: "Analysis of asset age based on purchase dates. Helps plan refresh cycles and track fleet aging.",
  icon: AccessTimeIcon,
  
  report_filters: [
    "asset_site",
    "type_asset",
    "status_asset",
    "purchase_date",
    "model",
  ],
  
  dataSources: [
    {
      id: "default",
      dbTable: "Assets",
      groupBy: ["purchase_date", "asset_site", "type_asset"],
    },
  ],
  
  charts: [
    {
      id: "byPurchaseYear",
      type: "bar",
      dimensions: ["purchase_date"],
      title: "Assets by Purchase Year",
      tooltip: "Distribution of assets based on purchase date. Assets without a purchase date excluded.",
      width: "half",
      dateAggregation: ["year"],
    },
    {
      id: "byDeviceType",
      type: "pie",
      dimensions: ["type_asset"],
      title: "Assets by Device Type",
      tooltip: "Device type distribution across the inventory.",
      width: "half",
    },
    {
      id: "purchasesByYearAndSite",
      type: "multi-line",
      dimensions: ["purchase_date", "asset_site"],
      title: "Asset Purchases by Year and Site",
      tooltip: "Purchase trends over time for each site.",
      width: "full",
      dateAggregation: ["month"],
    },
    {
      id: "inventoryAgingTable",
      type: "export-table",
      dimensions: ["asset_site", "type_asset", "purchase_date", "count"],
      dateAggregation: ["year", "count"],
      title: "Inventory Aging Summary",
      tooltip: "Complete inventory data grouped by purchase date, site, and asset type.",
      width: "full",
      dateField: "purchase_date",
      reportName: "Inventory Aging",
      exportFilename: "inventory-aging-detail.csv",
    },
  ],
  
  statCards: [
    {
      id: "totalAssets",
      title: "Total Assets",
      icon: "BuildIcon",
      color: "primary",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Total count of all assets in inventory.",
    },
    {
      id: "assetsLastYear",
      title: "Assets Last Year",
      icon: "HistoryIcon",
      color: "warning",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Assets purchased in the previous calendar year.",
      period: "last-year",
    },
    {
      id: "assetsLast365Days",
      title: "Assets Last 365 Days",
      icon: "UpdateIcon",
      color: "secondary",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Assets purchased in the last 365 days.",
      period: "last-365-days",
    },
    {
      id: "assetsThisYear",
      title: "Assets This Year",
      icon: "EventIcon",
      color: "success",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Assets purchased in the current calendar year.",
      period: "current-year",
    },
  ],
} as const satisfies ConfigDrivenReportConfig;

export const useInventoryAging = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(INVENTORY_AGING_CONFIG);

// ============================================
// WARRANTY STATUS
// ============================================

export const WARRANTY_STATUS_CONFIG = {
  id: "warrantyStatus",
  title: "Warranty Status",
  description: "Overview of warranty coverage and expiration status across assets. Helps identify coverage gaps and plan maintenance.",
  icon: VerifiedIcon,
  
  report_filters: [
    "asset_site",
    "type_asset",
    "status_asset",
    "warranty_expiration_date",
    "under_warranty",
    "model",
  ],
  
  dataSources: [
    {
      id: "default",
      dbTable: "Assets",
      groupBy: ["under_warranty", "asset_site", "type_asset"],
    },
  ],
  
  charts: [
    {
      id: "warrantyOverview",
      type: "pie",
      dimensions: ["under_warranty"],
      title: "Warranty Status Overview",
      tooltip: "Overall distribution of assets by warranty status.",
      width: "half",
    },
    {
      id: "warrantyByDeviceType",
      type: "stacked-bar",
      dimensions: ["under_warranty", "type_asset"],
      title: "Warranty Status by Device Type",
      tooltip: "Warranty coverage breakdown across different device types.",
      width: "half",
    },
    {
      id: "warrantyBySite",
      type: "heatmap",
      dimensions: ["asset_site", "under_warranty"],
      title: "Warranty Status by Site",
      tooltip: "Warranty coverage distribution across sites.",
      width: "full",
    },
    {
      id: "warrantyStatusTable",
      type: "export-table",
      dimensions: ["asset_site", "type_asset", "under_warranty", "count"],
      title: "Warranty Status Summary",
      tooltip: "Complete warranty coverage data grouped by site, asset type, and warranty status.",
      width: "full",
      reportName: "Warranty Status",
      exportFilename: "warranty-status-detail.csv",
    },
  ],
  
  statCards: [
    {
      id: "totalAssets",
      title: "Total Assets",
      icon: "BuildIcon",
      color: "primary",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Total count of all assets in inventory.",
    },
    {
      id: "underWarranty",
      title: "Under Warranty",
      icon: "CheckCircleIcon",
      color: "success",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        // under_warranty is a radio field storing "1"/"0" (raw meta value), not the "Yes"/"No" option labels
        column: "under_warranty",
        values: ["1"],
      },
      tooltip: "Assets currently under active warranty coverage.",
    },
    {
      id: "warrantyExpired",
      title: "No Warranty / Expired",
      icon: "WarningAmberIcon",
      color: "warning",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "under_warranty",
        values: ["0"],
      },
      tooltip: "Assets with expired or no warranty coverage.",
    },
    {
      id: "noWarrantyInfo",
      title: "Unknown Warranty Status",
      icon: "HelpIcon",
      color: "error",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "under_warranty",
        values: ["null"],
      },
      tooltip: "Assets with expired or no warranty coverage.",
    },
  ],
} as const satisfies ConfigDrivenReportConfig;

export const useWarrantyStatus = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(WARRANTY_STATUS_CONFIG);

// ============================================
// MISSING/UNASSIGNED
// ============================================

export const MISSING_UNASSIGNED_CONFIG = {
  id: "missingUnassigned",
  title: "Missing/Unassigned Assets",
  description: "Comprehensive view of assets with missing or unassigned status across sites and asset types.",
  icon: WarningAmberIcon,
  
  report_filters: [
    "asset_site",
    "type_asset",
    "status_asset",
    "model",
    "purchase_date",
  ],
  
  dataSources: [
    {
      id: "default",
      dbTable: "Assets",
      groupBy: ["type_asset", "asset_site", "status_asset"],
      filter: {
        column: "status_asset",
        values: ["missing", "unassigned"],
      },
    },
  ],
  
  charts: [
    {
      id: "statusBreakdown",
      type: "pie",
      dimensions: ["status_asset"],
      title: "Status Distribution",
      tooltip: "Breakdown of missing vs unassigned assets.",
      width: "half",
    },
    {
      id: "byType",
      type: "stacked-bar",
      dimensions: ["type_asset", "status_asset"],
      title: "Missing & Unassigned by Asset Type",
      tooltip: "Count breakdown by asset type, showing missing vs unassigned split.",
      width: "half",
    },
    {
      id: "missingByTypeAndSite",
      type: "heatmap",
      dimensions: ["type_asset", "asset_site" ],
      title: "Missing Assets by Site × Type",
      tooltip: "Heatmap showing distribution of MISSING assets across sites and types.",
      width: "half",
      filter: {
        column: "status_asset",
        values: ["missing"],
      },
    },
    {
      id: "unassignedByTypeAndSite",
      type: "heatmap",
      dimensions: ["asset_site", "type_asset"],
      title: "Unassigned Assets by Site × Type",
      tooltip: "Heatmap showing distribution of UNASSIGNED assets across sites and types.",
      width: "half",
      filter: {
        column: "status_asset",
        values: ["unassigned"],
      },
    },
    {
      id: "exportDefault",
      type: "export-table",
      dimensions: ["asset_site", "type_asset", "status_asset", "count"],
      title: "Missing/Unassigned Assets Export",
      tooltip: "Complete list of missing and unassigned assets.",
      width: "full",
      reportName: "Missing/Unassigned Assets",
      exportFilename: "missing-unassigned-assets.csv",
    },
  ],
  
  statCards: [
    {
      id: "totalMissing",
      title: "Total Missing/Unassigned",
      icon: "WarningAmberIcon",
      color: "error",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Total count of assets with missing or unassigned status.",
    },
    {
      id: "missingCount",
      title: "Missing",
      icon: "HelpOutlineIcon",
      color: "warning",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "status_asset",
        values: ["missing"],
      },
      tooltip: "Assets marked as missing.",
    },
    {
      id: "unassignedCount",
      title: "Unassigned",
      icon: "AssignmentIcon",
      color: "info",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "status_asset",
        values: ["unassigned"],
      },
      tooltip: "Assets marked as unassigned.",
    },
  ],
} as const satisfies ConfigDrivenReportConfig;

export const useMissingUnassigned = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(MISSING_UNASSIGNED_CONFIG);

// ============================================
// ASSETS BY SITE
// ============================================

export const ASSETS_BY_SITE_CONFIG = {
  id: "assetsBySite",
  title: "Assets by Site",
  description: "Geographic distribution of assets across sites.",
  icon: LocationOnIcon,
  
  report_filters: [
    "asset_site",
    "type_asset",
    "status_asset",
    "funding_source",
    "model",
  ],
  
  dataSources: [
    {
      id: "default",
      dbTable: "Assets",
      groupBy: ["asset_site", "type_asset", "status_asset"],
    },
  ],
  charts: [
    {
      id: "bySite",
      type: "bar",
      dimensions: ["asset_site"],
      title: "Assets by Site",
      tooltip: "Total asset count per site across all device types and statuses.",
      width: "half",
      sort: { order: "alphabetical" },
    },
    {
      id: "byAssetTypeByStatus",
      type: "stacked-bar",
      dimensions: ["asset_site", "type_asset"],
      title: "Asset Type Distribution by Site",
      tooltip: "Breakdown of device types per site. Each bar segment represents one device type.",
      width: "half",
      sort: { order: "alphabetical" },
    },
    {
      id: "byStatusBySite",
      type: "heatmap",
      dimensions: ["asset_site", "status_asset"],
      title: "Asset Status by Site",
      tooltip: "Asset status breakdown per site. Darker cells indicate higher counts.",
      width: "full",
    },
    {
      id: "byStatusSimple",
      type: "bar",
      dimensions: ["status_asset"],
      title: "Asset Status Distribution",
      tooltip: "Total count by asset status, calculated from primary data.",
      width: "half",
      sort: { order: "high-to-low" },
    },
    {
      id: "exportDefault",
      type: "export-table",
      dimensions: ["asset_site", "type_asset", "status_asset", "count"],
      title: "Assets by Site Export Table",
      tooltip: "Complete asset data table for export.",
      width: "full",
      reportName: "Assets by Site",
      exportFilename: "assets-by-site-detail.csv",
    },
  ],
  statCards: [
    {
      id: "totalAssets",
      title: "Total Assets",
      icon: "DevicesIcon",
      color: "primary",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Total number of assets across all sites.",
    },
    {
      id: "totalSites",
      title: "Total Sites",
      icon: "LocationOnIcon",
      color: "info",
      dbTable: "Assets",
      aggregation: "count",
      dimensions: ["asset_site"],
      tooltip: "Number of unique sites with assets.",
    },
    {
      id: "avgAssetsPerSite",
      title: "Avg Assets / Site",
      icon: "WarehouseIcon",
      color: "warning",
      dbTable: "Assets",
      aggregation: "average",
      dimensions: ["count", "asset_site"],
      tooltip: "Average number of assets per site.",
    },
  ],
} as const satisfies ConfigDrivenReportConfig;

export const useAssetsBySiteReport = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(ASSETS_BY_SITE_CONFIG);

// ============================================
// PURCHASE REPORT
// ============================================

export const PURCHASE_REPORT_CONFIG = {
  id: "purchaseReport",
  title: "Purchase Report",
  description: "Overview of asset purchase values and trends by asset type, site, and date for financial planning and budget tracking.",
  icon: AttachMoneyIcon,
  
  report_filters: [
    "asset_site",
    "type_asset",
    "purchase_date",
    "funding_source",
    "model",
  ],
  
  dataSources: [
    {
      id: "default",
      dbTable: "Assets",
      groupBy: ["type_asset", "asset_site", "purchase_date", "funding_source"],
      sum: ["cost"],
    },
  ],
  charts: [
    {
      id: "byYear",
      type: "bar",
      dimensions: ["purchase_date"],
      title: "Purchase Value by Year",
      tooltip: "Annual purchase value totals.",
      width: "half",
      dateAggregation: ["year"],
    },
    {
      id: "byType",
      type: "pie",
      dimensions: ["type_asset"],
      title: "Purchases by Asset Type",
      tooltip: "Distribution of purchase value across device types.",
      width: "half",
    },
    {
      id: "byMonthAndSite",
      type: "multi-line",
      dimensions: ["purchase_date", "asset_site"],
      title: "Purchase Value by Month and Site",
      tooltip: "Monthly purchase value trends for each site.",
      width: "full",
      dateAggregation: ["month"],
    },
    {
      id: "byYearAndType",
      type: "stacked-bar",
      dimensions: ["purchase_date", "type_asset"],
      dateField: "purchase_date",
      title: "Yearly Purchases by Type",
      tooltip: "Annual purchase value breakdown by asset type.",
      width: "half",
      dateAggregation: ["year"],
    },
    {
      id: "byFundingSource",
      type: "pie",
      dimensions: ["funding_source"],
      title: "Purchases by Funding Source",
      tooltip: "Distribution of purchase value by funding source.",
      width: "half",
    },
    {
      id: "bySiteAndType",
      type: "heatmap",
      dimensions: ["asset_site", "type_asset"],
      title: "Purchase Distribution: Site × Type",
      tooltip: "Heatmap showing purchase values across sites and asset types.",
      width: "full",
    },
    {
      id: "purchaseSummaryTable",
      type: "export-table",
      dimensions: ["asset_site", "type_asset", "purchase_date", "funding_source", "cost", "count"],
      dateAggregation: ["year", "cost", "count"],
      dateField: "purchase_date",
      title: "Purchase Summary Table",
      tooltip: "Complete purchase data table for analysis and export.",
      width: "full",
      reportName: "Purchase Report",
      exportFilename: "purchase-report-detail.csv",
    },
  ],
  statCards: [
    {
      id: "totalPurchaseValue",
      title: "Total Purchase Value",
      icon: "AttachMoneyIcon",
      color: "primary",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["cost"],
      prefix: "$",
      tooltip: "Total value of all purchases.",
    },
    {
      id: "averagePurchasePrice",
      title: "Average Purchase Price",
      icon: "PriceCheckIcon",
      color: "info",
      dbTable: "Assets",
      aggregation: "average",
      dimensions: ["cost", "count"],
      prefix: "$",
      tooltip: "Average cost per asset type.",
    },
    {
      id: "lastMonthPurchases",
      title: "Last Month's Purchases",
      icon: "CalendarMonthIcon",
      color: "warning",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["cost"],
      period: "last-month",
      prefix: "$",
      tooltip: "Total purchase value from last month.",
    },
    {
      id: "lastYearPurchases",
      title: "Last Year's Purchases",
      icon: "HistoryIcon",
      color: "success",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["cost"],
      period: "last-year",
      prefix: "$",
      tooltip: "Total purchase value from last year.",
    },
  ]
} as const satisfies ConfigDrivenReportConfig;

export const usePurchaseReport = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(PURCHASE_REPORT_CONFIG);

// ============================================
// ASSIGNMENT ROSTER
// ============================================

export const ASSIGNMENT_ROSTER_CONFIG = {
  id: "assignmentRoster",
  title: "Assignment Roster",
  description: "Comprehensive view of assets assigned to students or loaned out across sites and asset types.",
  icon: AssignmentIcon,
  
  report_filters: [
    "type_asset",
    "model",
    "status_asset",
    "asset_site",
  ],
  
  dataSources: [
    {
      id: "default",
      dbTable: "Assets",
      groupBy: ["type_asset", "asset_site", "status_asset"],
      filter: {
        column: "status_asset",
        values: ["assigned_to_student", "loaned_out"],
      },
    },
  ],
  
  charts: [
    {
      id: "statusBreakdown",
      type: "pie",
      dimensions: ["status_asset"],
      title: "Assignment Status Distribution",
      tooltip: "Breakdown of assigned to student vs loaned out assets.",
      width: "half",
    },
    {
      id: "byType",
      type: "stacked-bar",
      dimensions: ["type_asset", "status_asset"],
      title: "Assigned & Loaned by Asset Type",
      tooltip: "Count breakdown by asset type, showing assigned vs loaned split.",
      width: "half",
    },
    {
      id: "assignedByTypeAndSite",
      type: "heatmap",
      dimensions: ["asset_site", "type_asset"],
      title: "Assigned to Student by Site × Type",
      tooltip: "Heatmap showing distribution of ASSIGNED TO STUDENT assets across sites and types.",
      width: "half",
      filter: {
        column: "status_asset",
        values: ["assigned_to_student"],
      },
    },
    {
      id: "loanedByTypeAndSite",
      type: "heatmap",
      dimensions: ["asset_site", "type_asset"],
      title: "Loaned Out by Site × Type",
      tooltip: "Heatmap showing distribution of LOANED OUT assets across sites and types.",
      width: "half",
      filter: {
        column: "status_asset",
        values: ["loaned_out"],
      },
    },
    {
      id: "exportDefault",
      type: "export-table",
      dimensions: ["asset_site", "type_asset", "status_asset", "count"],
      title: "Assignment Roster Export",
      tooltip: "Complete list of assigned and loaned out assets.",
      width: "full",
      reportName: "Assignment Roster",
      exportFilename: "assignment-roster.csv",
    },
  ],
  
  statCards: [
    {
      id: "totalAssigned",
      title: "Total Assigned/Loaned",
      icon: "AssignmentIcon",
      color: "primary",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Total count of assigned or loaned out assets.",
    },
    {
      id: "assignedToStudentCount",
      title: "Assigned to Student",
      icon: "SchoolIcon",
      color: "success",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "status_asset",
        values: ["assigned_to_student"],
      },
      tooltip: "Assets assigned to students.",
    },
    {
      id: "loanedOutCount",
      title: "Loaned Out",
      icon: "LocalShippingIcon",
      color: "info",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "status_asset",
        values: ["loaned_out"],
      },
      tooltip: "Assets loaned to external parties.",
    },
  ],
} as const satisfies ConfigDrivenReportConfig;

export const useAssignmentRoster = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(ASSIGNMENT_ROSTER_CONFIG);

// ============================================
// REPAIR REPORT
// ============================================

export const REPAIR_REPORT_CONFIG = {
  id: "repairReport",
  title: "Repair Report",
  description: "Comprehensive view of assets in repair pipeline across sites and asset types.",
  icon: BuildIcon,
  
  report_filters: [
    "asset_site",
    "type_asset",
    "status_asset",
    "model",
  ],
  
  dataSources: [
    {
      id: "default",
      dbTable: "Assets",
      groupBy: ["type_asset", "asset_site", "status_asset"],
      filter: {
        column: "status_asset",
        values: ["awaiting_repair", "in_repair", "out_for_delivery"],
      },
    },
  ],
  
  charts: [
    {
      id: "statusBreakdown",
      type: "pie",
      dimensions: ["status_asset"],
      title: "Repair Status Distribution",
      tooltip: "Breakdown of awaiting repair, in repair, and out for delivery assets.",
      width: "half",
    },
    {
      id: "byType",
      type: "stacked-bar",
      dimensions: ["type_asset", "status_asset"],
      title: "Repairs by Asset Type",
      tooltip: "Count breakdown by asset type, showing repair status split.",
      width: "half",
    },
    {
      id: "bySite",
      type: "stacked-bar",
      dimensions: ["asset_site", "status_asset"],
      title: "Repairs by Site",
      tooltip: "Count breakdown by site, showing repair status split.",
      width: "full",
    },
    {
      id: "bySiteAndType",
      type: "heatmap",
      dimensions: ["asset_site", "type_asset"],
      title: "Repairs by Site & Asset Type",
      tooltip: "Heatmap showing distribution of assets in repair across sites and types.",
      width: "full",
    },
    {
      id: "exportDefault",
      type: "export-table",
      dimensions: ["asset_site", "type_asset", "status_asset", "count"],
      title: "Repair Pipeline Export",
      tooltip: "Complete list of assets in repair pipeline.",
      width: "full",
      reportName: "Repair Report",
      exportFilename: "repair-report.csv",
    },
  ],
  
  statCards: [
    {
      id: "totalInPipeline",
      title: "Total in Pipeline",
      icon: "BuildIcon",
      color: "primary",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Total count of assets in repair pipeline.",
    },
    {
      id: "awaitingRepairCount",
      title: "Awaiting Repair",
      icon: "ScheduleIcon",
      color: "warning",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "status_asset",
        values: ["awaiting_repair"],
      },
      tooltip: "Assets identified for repair but not yet started.",
    },
    {
      id: "inRepairCount",
      title: "In Repair",
      icon: "BuildCircleIcon",
      color: "info",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "status_asset",
        values: ["in_repair"],
      },
      tooltip: "Assets currently being repaired.",
    },
    {
      id: "outForDeliveryCount",
      title: "Out for Delivery",
      icon: "LocalShippingIcon",
      color: "success",
      dbTable: "Assets",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: "status_asset",
        values: ["out_for_delivery"],
      },
      tooltip: "Repaired assets being returned.",
    },
  ],
} as const satisfies ConfigDrivenReportConfig;

export const useRepairReport = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(REPAIR_REPORT_CONFIG);

// ============================================
// USER ENROLLMENT OVERVIEW
// ============================================

export const USER_ENROLLMENT_CONFIG = {
  id: "userEnrollment",
  title: "User Enrollment Overview",
  description: "Overview of user enrollment status, grades, and roles across the district.",
  icon: PeopleIcon,

  // report_filters intentionally omitted: filter option hydration for users
  // requires /aggregate/users endpoint — not yet wired into FilterOptionsProvider.

  dataSources: [
    {
      id: "default",
      dbTable: "Users",
      groupBy: [USER_FIELD_KEYS.ENROLLMENT_STATUS, USER_FIELD_KEYS.ROLE, USER_FIELD_KEYS.GRADE],
    },
  ],

  charts: [
    {
      id: "byEnrollmentStatus",
      type: "pie",
      dimensions: [USER_FIELD_KEYS.ENROLLMENT_STATUS],
      title: "Users by Enrollment Status",
      tooltip: "Distribution of all users by enrollment status.",
      width: "half",
    },
    {
      id: "byRoleType",
      type: "pie",
      dimensions: [USER_FIELD_KEYS.ROLE],
      title: "Users by Role",
      tooltip: "Breakdown of users across role types.",
      width: "half",
    },
    {
      id: "byGrade",
      type: "bar",
      dimensions: [USER_FIELD_KEYS.GRADE],
      title: "Students by Grade",
      tooltip: "Count of students per grade level.",
      width: "half",
      sort: { order: "alphabetical" },
    },
    {
      id: "statusByRole",
      type: "stacked-bar",
      dimensions: [USER_FIELD_KEYS.ROLE, USER_FIELD_KEYS.ENROLLMENT_STATUS],
      title: "Enrollment Status by Role",
      tooltip: "Breakdown of enrollment status across each role type.",
      width: "half",
    },
    {
      id: "exportDefault",
      type: "export-table",
      dimensions: [USER_FIELD_KEYS.ENROLLMENT_STATUS, USER_FIELD_KEYS.ROLE, USER_FIELD_KEYS.GRADE, "count"],
      title: "User Enrollment Export",
      tooltip: "Complete user enrollment data grouped by status, role, and grade.",
      width: "full",
      reportName: "User Enrollment Overview",
      exportFilename: "user-enrollment-overview.csv",
    },
  ],

  statCards: [
    {
      id: "totalUsers",
      title: "Total Users",
      icon: "PeopleIcon",
      color: "primary",
      dbTable: "Users",
      aggregation: "sum",
      dimensions: ["count"],
      tooltip: "Total number of users in the system.",
    },
    {
      id: "activeUsers",
      title: "Active Users",
      icon: "CheckCircleIcon",
      color: "success",
      dbTable: "Users",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: USER_FIELD_KEYS.ENROLLMENT_STATUS,
        values: ["active"],
      },
      tooltip: "Users with active enrollment status.",
    },
    {
      id: "studentCount",
      title: "Students",
      icon: "SchoolIcon",
      color: "info",
      dbTable: "Users",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: USER_FIELD_KEYS.ROLE,
        values: ["student"],
      },
      tooltip: "Total number of students.",
    },
    {
      id: "staffAndAdminCount",
      title: "Staff & Admin",
      icon: "BadgeIcon",
      color: "secondary",
      dbTable: "Users",
      aggregation: "sum",
      dimensions: ["count"],
      filter: {
        column: USER_FIELD_KEYS.ROLE,
        values: ["staff", "admin"],
      },
      tooltip: "Total number of staff and admin users.",
    },
  ],
} as const satisfies ConfigDrivenReportConfig;

export const useUserEnrollmentReport = (): ConfigDrivenReportResult =>
  useConfigDrivenReport(USER_ENROLLMENT_CONFIG);

/**
 * ========================================
 * REPORTS OBJECT - Single Source of Truth
 * ========================================
 * 
 * This object can be used to:
 * - Generate dynamic report navigation
 * - Apply org-level customizations (filter override, report enable/disable)
 * - Iterate through all reports programmatically
 */
export const REPORTS = {
  inventoryAging: INVENTORY_AGING_CONFIG,
  warrantyStatus: WARRANTY_STATUS_CONFIG,
  missingUnassigned: MISSING_UNASSIGNED_CONFIG,
  assetsBySite: ASSETS_BY_SITE_CONFIG,
  purchaseReport: PURCHASE_REPORT_CONFIG,
  assignmentRoster: ASSIGNMENT_ROSTER_CONFIG,
  repairReport: REPAIR_REPORT_CONFIG,
  userEnrollment: USER_ENROLLMENT_CONFIG,
} as const;
